import type { ImageDto } from '@sbg/shared/schemas';

type ImageSource = ImageDto['sources'][number];
export type ImageFormat = ImageSource['format'];

/** `url 480w, url 960w` for one format, narrowest first. Empty when the image has no such format. */
export function srcSetFor(sources: readonly ImageSource[], format: ImageFormat): string {
  return sources
    .filter((source) => source.format === format)
    .sort((a, b) => a.w - b.w)
    .map((source) => `${source.url} ${String(source.w)}w`)
    .join(', ');
}

/**
 * The `<img src>` for browsers that read neither `<source>` nor `srcset` well: the most widely
 * supported format that exists (JPEG → WebP → AVIF), at the width closest to 800 px.
 */
export function fallbackSource(sources: readonly ImageSource[]): ImageSource | undefined {
  for (const format of ['jpeg', 'webp', 'avif'] as const) {
    const candidates = sources.filter((source) => source.format === format);
    if (candidates.length === 0) continue;
    return candidates.reduce((best, source) =>
      Math.abs(source.w - 800) < Math.abs(best.w - 800) ||
      (Math.abs(source.w - 800) === Math.abs(best.w - 800) && source.w > best.w)
        ? source
        : best,
    );
  }
  return undefined;
}
